import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CALL_OUTCOMES,
  formatWhen,
  salesCrmApi,
  STATUS_LABELS,
} from "./api";
import "./salescrm.css";

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const SalesOpportunityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [opp, setOpp] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [status, setStatus] = useState("new");
  const [statusNotes, setStatusNotes] = useState("");
  const [callOutcome, setCallOutcome] = useState("connected");
  const [callNotes, setCallNotes] = useState("");
  const [callDuration, setCallDuration] = useState("");
  const [note, setNote] = useState("");
  const [followAt, setFollowAt] = useState("");
  const [followReason, setFollowReason] = useState("");
  const [waDetails, setWaDetails] = useState(
    "Shared Casting & Production package presentation"
  );

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [meRes, oppRes] = await Promise.all([
        salesCrmApi.me(),
        salesCrmApi.getOpportunity(id),
      ]);
      setMe(meRes.data);
      setOpp(oppRes.data);
      setStatus(oppRes.data.status);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load opportunity.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const run = async (fn, okMsg) => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await fn();
      setMessage(okMsg);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Action failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="child-sidenav">
        <div className="childnav-content scrm-page">Loading customer…</div>
      </div>
    );
  }
  if (!opp) {
    return (
      <div className="child-sidenav">
        <div className="childnav-content scrm-page">
          <div className="scrm-alert err">{error || "Not found"}</div>
          <button
            type="button"
            className="scrm-btn scrm-btn-outline"
            onClick={() => navigate("/dashboard/sales-crm/leads")}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  const c = opp.customer || {};
  const tel = c.mobile_number || "";

  return (
    <div className="child-sidenav">
      <div className="childnav-content scrm-page">
        <div className="scrm-header">
          <div>
            <h2>{c.full_name || "Customer"}</h2>
            <p>
              {c.hcc_id} · Opportunity #{opp.id} ·{" "}
              {opp.source_name || opp.source_code}
            </p>
          </div>
          <div className="scrm-actions">
            <button
              type="button"
              className="scrm-btn scrm-btn-outline"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
            {tel && (
              <a className="scrm-btn scrm-btn-primary" href={`tel:${tel}`}>
                Call {tel}
              </a>
            )}
            {tel && (
              <a
                className="scrm-btn scrm-btn-ghost"
                href={`https://wa.me/91${String(tel).replace(/\D/g, "").slice(-10)}`}
                target="_blank"
                rel="noreferrer"
              >
                WhatsApp
              </a>
            )}
            {c.hcc_id && (
              <button
                type="button"
                className="scrm-btn scrm-btn-ghost"
                onClick={() =>
                  window.open(`/dashboard/users/${c.hcc_id}/edit`, "_blank")
                }
              >
                Full ICC profile
              </button>
            )}
          </div>
        </div>

        {message && <div className="scrm-alert ok">{message}</div>}
        {error && <div className="scrm-alert err">{error}</div>}

        <div className="scrm-detail-grid">
          <div>
            <div className="scrm-card">
              <div className="scrm-profile-head">
                {c.profile_pic ? (
                  <img className="scrm-avatar" src={c.profile_pic} alt="" />
                ) : (
                  <div className="scrm-avatar placeholder">
                    {(c.full_name || "?").charAt(0)}
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 700, fontSize: "1.1rem" }}>
                    {c.full_name}
                  </div>
                  <div className="scrm-muted">
                    {c.city}
                    {c.city && c.state ? ", " : ""}
                    {c.state}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <span className={`scrm-pill ${opp.status}`}>
                      {STATUS_LABELS[opp.status]}
                    </span>{" "}
                    {opp.priority === "hot" && (
                      <span className="scrm-pill hot">Hot</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="scrm-kv">
                <div className="k">Mobile</div>
                <div>{c.mobile_number || "—"}</div>
                <div className="k">Email</div>
                <div>{c.email || "—"}</div>
                <div className="k">Registered</div>
                <div>{formatWhen(c.registration_date)}</div>
                <div className="k">Account</div>
                <div>
                  {c.account_status}
                  {c.is_pro ? " · Pro talent" : ""}
                  {c.is_verified ? " · Verified" : ""}
                </div>
                <div className="k">Activity</div>
                <div>
                  Views {c.views ?? 0} · Shortlists {c.shortlists ?? 0} ·
                  Followers {c.followers ?? 0}
                </div>
              </div>
              {c.crafts?.length ? (
                <div className="scrm-chips">
                  {c.crafts.map((craft) => (
                    <span className="scrm-chip" key={craft}>
                      {craft}
                    </span>
                  ))}
                </div>
              ) : null}
              {c.biography ? (
                <p className="scrm-muted" style={{ marginTop: 12 }}>
                  {c.biography}
                </p>
              ) : null}
            </div>

            <div className="scrm-card">
              <h3>Recent orders</h3>
              {opp.recent_orders?.length ? (
                <table className="scrm-table">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opp.recent_orders.map((o) => (
                      <tr key={o.id}>
                        <td>
                          {o.plan}{" "}
                          <span className="scrm-muted">{o.plan_for}</span>
                        </td>
                        <td>₹{o.amount}</td>
                        <td>{o.status}</td>
                        <td>{formatWhen(o.date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="scrm-muted">No orders yet.</div>
              )}
            </div>

            <div className="scrm-card">
              <h3>Previous auditions posted</h3>
              {opp.recent_auditions?.length ? (
                <ul className="scrm-timeline">
                  {opp.recent_auditions.map((a) => (
                    <li key={a.id}>
                      <div className="when">{formatWhen(a.date)}</div>
                      <div className="what">{a.title}</div>
                      <div className="details">{a.status}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="scrm-muted">No auditions posted.</div>
              )}
            </div>

            <div className="scrm-card">
              <h3>Customer interaction timeline</h3>
              {opp.timeline?.length ? (
                <ul className="scrm-timeline">
                  {opp.timeline.map((ev) => (
                    <li key={ev.id}>
                      <div className="when">
                        {formatWhen(ev.created_at)}
                        {ev.employee_name ? ` · ${ev.employee_name}` : ""}
                      </div>
                      <div className="what">{ev.title}</div>
                      {ev.details ? (
                        <div className="details">{ev.details}</div>
                      ) : null}
                      {(ev.previous_value || ev.new_value) && (
                        <div className="details">
                          {ev.previous_value || "—"} → {ev.new_value || "—"}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="scrm-muted">No activity yet.</div>
              )}
            </div>

            <div className="scrm-card">
              <h3>Status change history</h3>
              {opp.status_history?.length ? (
                <table className="scrm-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>From</th>
                      <th>To</th>
                      <th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opp.status_history.map((h) => (
                      <tr key={h.id}>
                        <td>{formatWhen(h.changed_at)}</td>
                        <td>{STATUS_LABELS[h.previous_status] || h.previous_status || "—"}</td>
                        <td>{STATUS_LABELS[h.new_status] || h.new_status}</td>
                        <td>{h.changed_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="scrm-muted">No status changes yet.</div>
              )}
            </div>
          </div>

          <div>
            <div className="scrm-card">
              <h3>Log a call</h3>
              <div className="scrm-form">
                <label>
                  Outcome
                  <select
                    value={callOutcome}
                    onChange={(e) => setCallOutcome(e.target.value)}
                  >
                    {CALL_OUTCOMES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Duration (seconds)
                  <input
                    value={callDuration}
                    onChange={(e) => setCallDuration(e.target.value)}
                    placeholder="e.g. 120"
                  />
                </label>
                <label>
                  Notes
                  <textarea
                    value={callNotes}
                    onChange={(e) => setCallNotes(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="scrm-btn scrm-btn-primary"
                  disabled={saving}
                  onClick={() =>
                    run(
                      () =>
                        salesCrmApi.logCall(opp.id, {
                          outcome: callOutcome,
                          notes: callNotes,
                          duration_seconds: Number(callDuration) || 0,
                        }),
                      "Call logged."
                    )
                  }
                >
                  Save call
                </button>
              </div>
            </div>

            <div className="scrm-card">
              <h3>Update status</h3>
              <div className="scrm-form">
                <label>
                  Status
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Reason / notes
                  <textarea
                    value={statusNotes}
                    onChange={(e) => setStatusNotes(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="scrm-btn scrm-btn-primary"
                  disabled={saving}
                  onClick={() =>
                    run(
                      () =>
                        salesCrmApi.updateStatus(opp.id, {
                          status,
                          notes: statusNotes,
                          reason: statusNotes,
                        }),
                      "Status updated."
                    )
                  }
                >
                  Save status
                </button>
              </div>
            </div>

            <div className="scrm-card">
              <h3>Schedule follow-up</h3>
              <div className="scrm-form">
                <label>
                  Date & time
                  <input
                    type="datetime-local"
                    value={followAt}
                    onChange={(e) => setFollowAt(e.target.value)}
                  />
                </label>
                <label>
                  Reason
                  <input
                    value={followReason}
                    onChange={(e) => setFollowReason(e.target.value)}
                    placeholder="Customer asked to call after shoot"
                  />
                </label>
                <button
                  type="button"
                  className="scrm-btn scrm-btn-outline"
                  disabled={saving || !followAt}
                  onClick={() =>
                    run(async () => {
                      const due = new Date(followAt);
                      await salesCrmApi.scheduleFollowUp(opp.id, {
                        due_at: due.toISOString(),
                        reason: followReason,
                      });
                    }, "Follow-up scheduled.")
                  }
                >
                  Schedule
                </button>
              </div>
              {opp.follow_ups?.filter((f) => f.status === "pending").length ? (
                <div style={{ marginTop: 12 }}>
                  <div className="scrm-muted" style={{ marginBottom: 6 }}>
                    Pending follow-ups
                  </div>
                  {opp.follow_ups
                    .filter((f) => f.status === "pending")
                    .map((f) => (
                      <div
                        key={f.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {formatWhen(f.due_at)}
                          </div>
                          <div className="scrm-muted">{f.reason || "—"}</div>
                        </div>
                        <button
                          type="button"
                          className="scrm-btn scrm-btn-ghost"
                          onClick={() =>
                            run(
                              () =>
                                salesCrmApi.completeFollowUp(
                                  f.id,
                                  "Completed from CRM"
                                ),
                              "Follow-up marked done."
                            )
                          }
                        >
                          Done
                        </button>
                      </div>
                    ))}
                </div>
              ) : null}
            </div>

            <div className="scrm-card">
              <h3>WhatsApp / note</h3>
              <div className="scrm-form">
                <label>
                  WhatsApp activity
                  <textarea
                    value={waDetails}
                    onChange={(e) => setWaDetails(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="scrm-btn scrm-btn-ghost"
                  disabled={saving}
                  onClick={() =>
                    run(
                      () => salesCrmApi.logWhatsapp(opp.id, waDetails),
                      "WhatsApp activity logged."
                    )
                  }
                >
                  Log WhatsApp
                </button>
                <label>
                  Internal note
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="scrm-btn scrm-btn-outline"
                  disabled={saving || !note.trim()}
                  onClick={() =>
                    run(
                      () => salesCrmApi.addNote(opp.id, note.trim()),
                      "Note saved."
                    )
                  }
                >
                  Save note
                </button>
              </div>
            </div>

            {me?.is_manager && (
              <div className="scrm-card">
                <h3>Manager actions</h3>
                <button
                  type="button"
                  className="scrm-btn scrm-btn-danger"
                  disabled={saving}
                  onClick={() =>
                    run(
                      () =>
                        salesCrmApi.reopen(opp.id, {
                          reason: "Manager reopen",
                          priority: "hot",
                        }),
                      "New opportunity created from this history."
                    )
                  }
                >
                  Reopen as new Hot opportunity
                </button>
                <p className="scrm-muted" style={{ marginTop: 8 }}>
                  Keeps this record in history and creates a fresh opportunity
                  for the same customer.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { SalesOpportunityDetail };
